import { useState, useEffect } from "react";
import BotonInstructivo from "./BotonInstructivo";
import { subirDatos, eliminarDato } from "./syncSupabase";
import {
  NATURA_STD_06482_SEED,
  estadoInicialFamilia, estadoInicialSeriePrograma, cerrarMes, nuevaFilaIndicadores, reabrirMes, cerrarFilaReabierta,
  registrarDesvioCritico, registrarResultadoAuditoriaMonitoreo, descontarVentanaGraciaSiCorresponde,
  construirPortfolioStats, ultimoValorConocido,
} from "./motorFaseCalidad";

const STORAGE_FAMILIAS = "alqos_familias";
const STORAGE_PROGRAMAS = "alqos_programas_calidad_cliente";
const STORAGE_FASES = "alqos_fase_familia_linea";
const STORAGE_INDICADORES = "alqos_indicadores_mensuales";

const estadosFamilia = { activa: { label: "✅ Activa", color: "#22c55e" }, inactiva: { label: "⛔ Inactiva", color: "#e63946" }, desarrollo: { label: "🔵 En desarrollo", color: "#3b82f6" } };
const coloresFase = { bronce: "#cd7f32", plata: "#808085", oro: "#f59e0b" };
const iconosFase = { bronce: "🥉", plata: "🥈", oro: "🥇" };

const storage = {
  get: (k, def = []) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { console.error(e); } },
};

function generarCod(lista) { return `FAM-${String(lista.length + 1).padStart(3, "0")}`; }
function periodoActual() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

// Hash simple y estable de una clave compuesta a un bigint positivo (evita colisión con ids
// Date.now()). fase_familia_linea no tiene id propio -> se sube/borra en Supabase con este id
// derivado de (idFamiliaLinea, idProgramaCliente).
function hashClaveCompuesta(clave) {
  let hash = 0;
  for (let i = 0; i < clave.length; i++) hash = (hash * 31 + clave.charCodeAt(i)) % 900719925474;
  return hash + 1;
}

// Migración additiva localStorage -> Supabase (FamiliasLineas nunca había sincronizado).
// Regla: NUNCA pisar ni borrar lo que ya está en Supabase. Si una família local no existe allá
// (match por nombre+clientePrincipal), se sube tal cual. Si existe pero con datos distintos, se
// marca como colisión para revisión manual — no se resuelve sola.
async function migrarFamiliasAdditivo(localFamilias, supabase) {
  const { data } = await supabase.from("familias").select("data");
  const remotas = data?.map(r => r.data) || [];
  const colisiones = [];
  const nuevasEnRemoto = [];
  const tenantId = Number(localStorage.getItem("alqos_tenant_id")) || null;
  const comparable = (f) => JSON.stringify({ ...f, id: undefined, fechaCreacion: undefined });
  for (const local of localFamilias) {
    const match = remotas.find(r => r.nombre === local.nombre && r.clientePrincipal === local.clientePrincipal);
    if (match) {
      if (comparable(match) !== comparable(local)) colisiones.push({ local, remota: match });
      continue; // ya existe en Supabase -> nunca se pisa
    }
    await supabase.from("familias").upsert({ id: local.id, data: local, tenant_id: tenantId });
    nuevasEnRemoto.push(local);
  }
  return { remotas: [...remotas, ...nuevasEnRemoto], colisiones };
}

// ppm = NC.cantidad (afectada) acumulada en el período / Produccion.cantidadProducida acumulada en el período × 1.000.000
// Es el único de los 5 indicadores que se puede derivar 100% de datos que ya existen en el sistema
// (NoConformidades.cantidad + Produccion.cantidadProducida, ambos ahora vinculados por idFamiliaLinea).
function sugerirPpm(idFamiliaLinea, periodo) {
  const ncs = storage.get("alqos_ncs");
  const produccion = storage.get("alqos_produccion");
  const enPeriodo = (fecha) => typeof fecha === "string" && fecha.slice(0, 7) === periodo;
  const rechazado = ncs.filter(n => n.idFamiliaLinea === idFamiliaLinea && enPeriodo(n.fecha)).reduce((acc, n) => acc + (Number(n.cantidad) || 0), 0);
  const producido = produccion.filter(p => p.idFamiliaLinea === idFamiliaLinea && enPeriodo(p.fecha)).reduce((acc, p) => acc + (Number(p.cantidadProducida) || 0), 0);
  if (producido === 0) return null;
  return Math.round((rechazado / producido) * 1_000_000);
}

export default function FamiliasLineas({ onVolver, oscuro, usuario }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [programas, setProgramas] = useState(() => storage.get(STORAGE_PROGRAMAS));
  const [fases, setFases] = useState(() => storage.get(STORAGE_FASES));
  const [indicadoresMensuales, setIndicadoresMensuales] = useState(() => storage.get(STORAGE_INDICADORES));
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ estado: "activa", participaPrograma: true });
  const [detalle, setDetalle] = useState(null);
  const [tabDetalle, setTabDetalle] = useState("datos");
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [formIndicadores, setFormIndicadores] = useState({ periodo: periodoActual() });
  const [colisionesMigracion, setColisionesMigracion] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState(() => new Set());
  const [confirmEliminarMultiple, setConfirmEliminarMultiple] = useState(false);

  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import("./supabase");
        const localFamilias = storage.get(STORAGE_FAMILIAS);
        const { remotas, colisiones } = await migrarFamiliasAdditivo(localFamilias, supabase);
        setColisionesMigracion(colisiones);
        if (colisiones.length === 0) {
          // Sin colisiones sin resolver: recién ahí es seguro que localStorage refleje la unión
          // (nunca se perdió nada — todo lo local que faltaba ya se subió de forma aditiva).
          setItems(remotas);
          storage.set(STORAGE_FAMILIAS, remotas);
        } else {
          // Hay colisiones: no tocar localStorage todavía. Se muestran remotas + las locales en
          // colisión (para que Adur las compare), pero NO se sobreescribe nada hasta resolverlas.
          const idsEnColision = new Set(colisiones.map(c => c.local.id));
          setItems([...remotas, ...localFamilias.filter(l => idsEnColision.has(l.id))]);
        }
        const [r2, r3] = await Promise.all([
          supabase.from("fase_familia_linea").select("data"),
          supabase.from("indicadores_mensuales_familia").select("data"),
        ]);
        setFases(r2.data?.length ? r2.data.map(r => r.data) : storage.get(STORAGE_FASES));
        setIndicadoresMensuales(r3.data?.length ? r3.data.map(r => r.data) : storage.get(STORAGE_INDICADORES));
      } catch {
        setItems(storage.get(STORAGE_FAMILIAS));
      }
      setCargando(false);
    }
    cargar();
  }, []);

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";
  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  if (cargando) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: bg }}>
      <p style={{ color: oscuro ? "white" : "#09184D", fontSize: "16px" }}>Cargando familias...</p>
    </div>
  );

  // ── FAMILIAS: CRUD ──
  const guardar = () => {
    const nuevo = { ...form, id: Date.now(), codigo: generarCod(items), fechaCreacion: new Date().toISOString(), creadoPor: usuario?.nombre || "Sistema" };
    const nuevos = [...items, nuevo];
    setItems(nuevos);
    storage.set(STORAGE_FAMILIAS, nuevos);
    subirDatos(STORAGE_FAMILIAS);
    setForm({ estado: "activa", participaPrograma: true });
    setVista("listado");
  };

  const eliminar = async (id) => {
    const nuevos = items.filter(i => i.id !== id);
    setItems(nuevos);
    storage.set(STORAGE_FAMILIAS, nuevos);
    await eliminarDato(STORAGE_FAMILIAS, id);
    setConfirmEliminar(null); setDetalle(null); setVista("listado");
  };

  const toggleSeleccion = (id) => {
    setSeleccionadas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Borrado en cascada de familias de prueba: la família + su fase vigente + TODOS sus meses de
  // indicadores. NC y Producción NO se tocan (quedan con idFamiliaLinea apuntando a un id que ya
  // no existe — aceptable, no se auto-borran NCs reales por limpiar familias de prueba).
  const eliminarFamiliasSeleccionadas = async () => {
    const ids = [...seleccionadas];

    const indicadoresABorrar = indicadoresMensuales.filter(i => ids.includes(i.idFamiliaLinea));
    const indicadoresRestantes = indicadoresMensuales.filter(i => !ids.includes(i.idFamiliaLinea));
    setIndicadoresMensuales(indicadoresRestantes);
    storage.set(STORAGE_INDICADORES, indicadoresRestantes);
    for (const fila of indicadoresABorrar) await eliminarDato(STORAGE_INDICADORES, fila.id);

    const fasesABorrar = fases.filter(f => ids.includes(f.idFamiliaLinea));
    const fasesRestantes = fases.filter(f => !ids.includes(f.idFamiliaLinea));
    setFases(fasesRestantes);
    storage.set(STORAGE_FASES, fasesRestantes);
    for (const f of fasesABorrar) await eliminarDatosConId("fase_familia_linea", `${f.idFamiliaLinea}-${f.idProgramaCliente}`);

    const nuevosItems = items.filter(i => !ids.includes(i.id));
    setItems(nuevosItems);
    storage.set(STORAGE_FAMILIAS, nuevosItems);
    for (const id of ids) await eliminarDato(STORAGE_FAMILIAS, id);

    setSeleccionadas(new Set());
    setConfirmEliminarMultiple(false);
    if (detalle && ids.includes(detalle.id)) { setDetalle(null); setVista("listado"); }
  };

  const actualizarFamilia = (id, campos) => {
    const nuevos = items.map(i => i.id === id ? { ...i, ...campos } : i);
    setItems(nuevos);
    storage.set(STORAGE_FAMILIAS, nuevos);
    subirDatos(STORAGE_FAMILIAS);
    if (detalle?.id === id) setDetalle(prev => ({ ...prev, ...campos }));
  };

  // ── MOTOR DE FASES: helpers de estado ──
  const obtenerOCrearFase = (idFamiliaLinea, idProgramaCliente) => {
    const existente = fases.find(f => f.idFamiliaLinea === idFamiliaLinea && f.idProgramaCliente === idProgramaCliente);
    return existente || estadoInicialFamilia(idFamiliaLinea, idProgramaCliente);
  };

  // Guarda uno o varios estados de fase a la vez (Segunda/Tercera Regla aplican a TODAS las
  // familias activas del programa, no a una sola — ver handleDesvioCritico/handleResultadoMonitoreo).
  const guardarFases = (nuevosEstados) => {
    let actualizadas = fases;
    for (const nuevoEstado of nuevosEstados) {
      const existe = actualizadas.some(f => f.idFamiliaLinea === nuevoEstado.idFamiliaLinea && f.idProgramaCliente === nuevoEstado.idProgramaCliente);
      actualizadas = existe
        ? actualizadas.map(f => (f.idFamiliaLinea === nuevoEstado.idFamiliaLinea && f.idProgramaCliente === nuevoEstado.idProgramaCliente) ? nuevoEstado : f)
        : [...actualizadas, nuevoEstado];
    }
    setFases(actualizadas);
    storage.set(STORAGE_FASES, actualizadas);
    // fase_familia_linea no tiene id propio estable -> se sube con un id derivado determinístico
    nuevosEstados.forEach(e => subirDatosConId(STORAGE_FASES, "fase_familia_linea", e, `${e.idFamiliaLinea}-${e.idProgramaCliente}`));
  };
  const guardarFase = (nuevoEstado) => guardarFases([nuevoEstado]);

  async function subirDatosConId(storageKey, tabla, dataObj, claveCompuesta) {
    try {
      const { supabase } = await import("./supabase");
      await supabase.from(tabla).upsert({ id: hashClaveCompuesta(claveCompuesta), data: dataObj, tenant_id: usuario?.tenant_id });
    } catch (e) { console.error(`Error subiendo ${storageKey}`, e); }
  }

  async function eliminarDatosConId(tabla, claveCompuesta) {
    try {
      const { supabase } = await import("./supabase");
      await supabase.from(tabla).delete().eq("id", hashClaveCompuesta(claveCompuesta));
    } catch (e) { console.error(`Error borrando de ${tabla}`, e); }
  }

  const programaDe = (familia) => programas.find(p => p.id === familia?.idProgramaCalidadCliente);
  const familiasActivasDelPrograma = (idProgramaCliente) => items.filter(it => it.idProgramaCalidadCliente === idProgramaCliente && it.participaPrograma !== false);
  // Defensivo: programas creados antes de este fix pueden no tener estadoSerie todavía.
  const estadoSerieDe = (programa) => programa?.estadoSerie || estadoInicialSeriePrograma();

  // ── CIERRE DE MES ──
  const handleCerrarMes = (familia) => {
    const programa = programaDe(familia);
    if (!programa) { alert("Esta familia no tiene un programa de calidad de cliente asignado."); return; }
    const periodo = formIndicadores.periodo;
    const yaExiste = indicadoresMensuales.some(i => i.idFamiliaLinea === familia.id && i.idProgramaCliente === programa.id && i.periodo === periodo && i.cerrado);
    if (yaExiste) { alert(`El período ${periodo} ya está cerrado para esta familia. Reabrilo primero si necesitás corregirlo.`); return; }

    const valoresMes = {
      ppm: formIndicadores.ppm !== undefined && formIndicadores.ppm !== "" ? Number(formIndicadores.ppm) : null,
      auditoriaTecnica: formIndicadores.auditoriaTecnica !== undefined && formIndicadores.auditoriaTecnica !== "" ? Number(formIndicadores.auditoriaTecnica) : null,
      cpk: formIndicadores.cpk !== undefined && formIndicadores.cpk !== "" ? Number(formIndicadores.cpk) : null,
      cumplimientoRNC: formIndicadores.cumplimientoRNC !== undefined && formIndicadores.cumplimientoRNC !== "" ? Number(formIndicadores.cumplimientoRNC) : null,
      reincidenciaRNC: formIndicadores.reincidenciaRNC !== undefined && formIndicadores.reincidenciaRNC !== "" ? Number(formIndicadores.reincidenciaRNC) : null,
    };

    const estadoPrevio = obtenerOCrearFase(familia.id, programa.id);
    // Portfolio con el ÚLTIMO Cpk conocido de cada família (no solo el de este período exacto) —
    // así el gate de ascenso no depende de en qué orden se cierra el mes de cada família.
    // La família que se está cerrando ahora usa su valor recién ingresado (todavía no está en
    // indicadoresMensuales, se sube después).
    const capabilidadPorFamilia = new Map();
    for (const f of fases.filter(x => x.idProgramaCliente === programa.id)) {
      const cpk = f.idFamiliaLinea === familia.id
        ? (valoresMes.cpk ?? ultimoValorConocido(indicadoresMensuales, f.idFamiliaLinea, "cpk", periodo))
        : ultimoValorConocido(indicadoresMensuales, f.idFamiliaLinea, "cpk", periodo);
      if (cpk !== null && cpk !== undefined) capabilidadPorFamilia.set(f.idFamiliaLinea, [{ cpk }]);
    }
    const portfolioStats = construirPortfolioStats(fases.filter(f => f.idProgramaCliente === programa.id), capabilidadPorFamilia);

    const { estado: nuevoEstado, eventos } = cerrarMes({ estado: estadoPrevio, valoresMes, programa, portfolioStats, periodo, metaAnualPpm: familia.metaAnualPpm ?? null });
    guardarFase(nuevoEstado);

    // Ventana de gracia Serie B es del PROGRAMA, no de la família — se descuenta una sola vez por
    // período sin importar cuántas familias cierren ese mes (ver descontarVentanaGraciaSiCorresponde).
    const { estadoSerie: nuevoEstadoSerie, evento: eventoSerie } = descontarVentanaGraciaSiCorresponde(estadoSerieDe(programa), periodo);
    if (nuevoEstadoSerie !== estadoSerieDe(programa)) actualizarPrograma(programa.id, { estadoSerie: nuevoEstadoSerie });
    const todosLosEventos = eventoSerie ? [...eventos, eventoSerie] : eventos;

    const fila = nuevaFilaIndicadores({ idFamiliaLinea: familia.id, idProgramaCliente: programa.id, periodo, valoresMes, usuario });
    const nuevasFilas = [...indicadoresMensuales, fila];
    setIndicadoresMensuales(nuevasFilas);
    storage.set(STORAGE_INDICADORES, nuevasFilas);
    subirDatos(STORAGE_INDICADORES);

    setFormIndicadores({ periodo: periodoActual() });
    if (todosLosEventos.length) alert(`Mes cerrado.\n\n${todosLosEventos.join("\n")}`);
    else alert("Mes cerrado. Sin cambios de fase.");
  };

  const handleReabrirMes = (fila) => {
    const motivo = prompt("Motivo de la reapertura (obligatorio, queda logueado para auditoría externa):");
    if (!motivo?.trim()) return;
    try {
      const filaReabierta = reabrirMes(fila, motivo, usuario);
      const nuevas = indicadoresMensuales.map(i => i.id === fila.id ? filaReabierta : i);
      setIndicadoresMensuales(nuevas);
      storage.set(STORAGE_INDICADORES, nuevas);
      subirDatos(STORAGE_INDICADORES);
    } catch (e) { alert(e.message); }
  };

  const handleCerrarFilaReabierta = (fila) => {
    const nuevas = indicadoresMensuales.map(i => i.id === fila.id ? cerrarFilaReabierta(fila, usuario) : i);
    setIndicadoresMensuales(nuevas);
    storage.set(STORAGE_INDICADORES, nuevas);
    subirDatos(STORAGE_INDICADORES);
    alert("Mes cerrado de nuevo. IMPORTANTE: la fase NO se recalculó automáticamente con el valor corregido — revisala manualmente y volvé a correr el cierre si corresponde.");
  };

  const handleMarcarFaseRevisada = (fila) => {
    const nuevas = indicadoresMensuales.map(i => i.id === fila.id ? { ...i, advertenciaFaseNoRecalculada: false } : i);
    setIndicadoresMensuales(nuevas);
    storage.set(STORAGE_INDICADORES, nuevas);
    subirDatos(STORAGE_INDICADORES);
  };

  // Segunda y Tercera Regla son a nivel proveedor-cliente completo: se aplican a TODAS las
  // familias activas del mismo programa, no solo a la família donde se originó el incidente.
  // La família de origen queda registrada en el historial de cada família afectada (trazabilidad).
  const handleDesvioCritico = (familia) => {
    const programa = programaDe(familia);
    if (!programa) return;
    const motivo = prompt("Descripción del desvío crítico (contaminación, incumplimiento legal, recall, etc.):");
    if (!motivo?.trim()) return;
    const activas = familiasActivasDelPrograma(programa.id);
    const estadosPrevios = activas.map(f => obtenerOCrearFase(f.id, programa.id));
    const nuevosEstados = registrarDesvioCritico(estadosPrevios, motivo, periodoActual(), familia.id);
    guardarFases(nuevosEstados);
    alert(`Desvío crítico registrado. ${nuevosEstados.length} família(s) activa(s) del programa llevadas a Bronce (Segunda Regla, alcance proveedor-cliente completo).`);
  };

  // Serie A/B es un solo estado por PROGRAMA (no por família) — opera sobre programa.estadoSerie.
  const handleResultadoMonitoreo = (familia) => {
    const programa = programaDe(familia);
    if (!programa) return;
    const resultado = prompt('Resultado de la auditoría de monitoreo: "satisfactorio", "parcialmente_satisfactorio" o "no_satisfactorio"');
    if (!["satisfactorio", "parcialmente_satisfactorio", "no_satisfactorio"].includes(resultado)) { alert("Valor inválido."); return; }
    const nuevoEstadoSerie = registrarResultadoAuditoriaMonitoreo(estadoSerieDe(programa), resultado, programa, periodoActual(), familia.id);
    actualizarPrograma(programa.id, { estadoSerie: nuevoEstadoSerie });
    alert("Resultado registrado (Tercera Regla, alcance proveedor-cliente completo — un solo estado de Serie para todo el programa).");
  };

  // ── PROGRAMAS DE CALIDAD CLIENTE (config) ──
  const seedNatura = () => {
    if (programas.some(p => p.codigo === NATURA_STD_06482_SEED.codigo)) { alert("Ya existe un programa Natura STD-06482 cargado."); return; }
    const nuevo = { ...NATURA_STD_06482_SEED, id: Date.now(), estadoSerie: estadoInicialSeriePrograma() };
    const nuevos = [...programas, nuevo];
    setProgramas(nuevos);
    storage.set(STORAGE_PROGRAMAS, nuevos);
    subirDatos(STORAGE_PROGRAMAS);
  };

  const actualizarPrograma = (id, campos) => {
    const nuevos = programas.map(p => p.id === id ? { ...p, ...campos } : p);
    setProgramas(nuevos);
    storage.set(STORAGE_PROGRAMAS, nuevos);
    subirDatos(STORAGE_PROGRAMAS);
  };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🏭</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar esta familia?</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (confirmEliminarMultiple) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "420px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🏭</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar {seleccionadas.size} família(s)?</p>
        <p style={{ fontSize: "12px", color: subtexto, marginBottom: "1rem" }}>Se borra también su fase vigente y todos sus meses de indicadores cargados. Las NC y registros de Producción que las referencian no se tocan.</p>
        <div style={{ maxHeight: "160px", overflowY: "auto", textAlign: "left", background: oscuro ? "#091840" : "#F9FAFB", borderRadius: "8px", padding: "10px 14px", marginBottom: "1rem" }}>
          {items.filter(i => seleccionadas.has(i.id)).map(i => (
            <p key={i.id} style={{ fontSize: "12px", color: texto, margin: "3px 0" }}>{i.codigo} — {i.nombre || "Sin nombre"}</p>
          ))}
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminarMultiple(false)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={eliminarFamiliasSeleccionadas} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar {seleccionadas.size}</button>
        </div>
      </div>
    </div>
  );

  // ── CONFIG DE PROGRAMAS ──
  if (vista === "config") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="⚙️ Programas de Calidad Cliente" onBack={() => setVista("listado")} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "760px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <p style={{ fontSize: "13px", color: texto, margin: "0 0 10px" }}>Cada programa define las fases, umbrales y reglas de retorno para un cliente. Es genérico: sirve para Natura y para cualquier otro cliente que pida algo parecido.</p>
          {esAdmin && <button onClick={seedNatura} style={{ padding: "9px 16px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Cargar Natura STD-06482 v6.0</button>}
        </div>
        {programas.length === 0
          ? <div style={{ textAlign: "center", padding: "3rem" }}><p style={{ fontSize: "40px" }}>⚙️</p><p style={{ fontSize: "14px", color: texto }}>Sin programas configurados</p></div>
          : programas.map(p => (
              <div key={p.id} style={card}>
                <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#7B5BF2", marginBottom: "10px" }}>{p.cliente} — {p.codigo} v{p.version}</h3>
                {["bronce", "plata", "oro"].map(fase => (
                  <div key={fase} style={{ marginBottom: "10px" }}>
                    <p style={{ fontSize: "12px", fontWeight: "700", color: coloresFase[fase], margin: "0 0 4px" }}>{iconosFase[fase]} {fase}</p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                      {["umbralAuditoriaAscenso", "umbralAuditoriaMonitoreo", "umbralAuditoriaMantenimiento"].map(campo => p.fases[fase]?.[campo] !== undefined && (
                        <div key={campo}>
                          <span style={{ fontSize: "10px", color: subtexto }}>{campo.replace("umbralAuditoria", "")}</span>
                          <input type="number" defaultValue={p.fases[fase][campo] ?? ""} disabled={!esAdmin}
                            onBlur={e => actualizarPrograma(p.id, { fases: { ...p.fases, [fase]: { ...p.fases[fase], [campo]: e.target.value ? Number(e.target.value) : null } } })}
                            style={{ ...inp, marginTop: "2px", padding: "5px 8px", fontSize: "12px" }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px", marginTop: "4px" }}>
                      {["cpk", "ppmReduccionPct"].map(campo => p.fases[fase]?.metas?.[campo] !== undefined && (
                        <div key={campo}>
                          <span style={{ fontSize: "10px", color: subtexto }}>{campo === "cpk" ? "meta Cpk" : "ppm: % reducción"}</span>
                          <input type="number" step="any" defaultValue={p.fases[fase].metas[campo] ?? ""} disabled={!esAdmin} placeholder="sin dato"
                            onBlur={e => actualizarPrograma(p.id, { fases: { ...p.fases, [fase]: { ...p.fases[fase], metas: { ...p.fases[fase].metas, [campo]: e.target.value ? Number(e.target.value) : null } } } })}
                            style={{ ...inp, marginTop: "2px", padding: "5px 8px", fontSize: "12px" }} />
                        </div>
                      ))}
                      {["cumplimientoRNC", "reincidenciaRNC"].map(indicador => p.fases[fase]?.metas?.[indicador] && (
                        ["implementacion", "monitoreo"].map(ctx => (
                          <div key={`${indicador}-${ctx}`}>
                            <span style={{ fontSize: "10px", color: subtexto }}>{indicador === "cumplimientoRNC" ? "cumpl.RNC" : "reincid.RNC"} ({ctx.slice(0, 4)})</span>
                            <input type="number" step="any" defaultValue={p.fases[fase].metas[indicador][ctx] ?? ""} disabled={!esAdmin} placeholder="sin dato"
                              onBlur={e => actualizarPrograma(p.id, { fases: { ...p.fases, [fase]: { ...p.fases[fase], metas: { ...p.fases[fase].metas, [indicador]: { ...p.fases[fase].metas[indicador], [ctx]: e.target.value ? Number(e.target.value) : null } } } } })}
                              style={{ ...inp, marginTop: "2px", padding: "5px 8px", fontSize: "12px" }} />
                          </div>
                        ))
                      ))}
                    </div>
                  </div>
                ))}
                <p style={{ fontSize: "11px", color: subtexto, marginTop: "8px" }}>La evaluación mensual usa siempre el valor "monitoreo" (régimen permanente). "Implementación" queda como referencia editable. ppm se calcula como Meta Anual de cada família × (1 − % de reducción) — cargá la Meta Anual en la pestaña "Datos" de cada família.</p>
              </div>
            ))
        }
      </div>
    </div>
  );

  // ── DETALLE ──
  if (vista === "detalle" && detalle) {
    const programa = programaDe(detalle);
    const estadoFase = programa ? obtenerOCrearFase(detalle.id, programa.id) : null;
    const estadoSerie = programa ? estadoSerieDe(programa) : null;
    const historialIndicadores = indicadoresMensuales.filter(i => i.idFamiliaLinea === detalle.id).sort((a, b) => b.periodo.localeCompare(a.periodo));

    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
        <Header titulo={detalle.codigo} onBack={() => { setDetalle(null); setVista("listado"); }}
          extra={esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)} style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>} />
        <div style={{ padding: "0 2rem 2rem", maxWidth: "760px", margin: "0 auto" }}>

          {/* Badge de fase calculada. Serie A/B es del PROGRAMA (proveedor-cliente completo), no de esta família. */}
          {programa && estadoFase && (
            <div style={{ ...card, textAlign: "center", background: `${coloresFase[estadoFase.faseActual]}15`, border: `2px solid ${coloresFase[estadoFase.faseActual]}` }}>
              <p style={{ fontSize: "40px", margin: "0 0 4px" }}>{iconosFase[estadoFase.faseActual]}</p>
              <p style={{ fontSize: "22px", fontWeight: "800", color: coloresFase[estadoFase.faseActual], margin: "0 0 4px", textTransform: "capitalize" }}>{estadoFase.faseActual} — Serie {estadoSerie.serie}</p>
              <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>{programa.cliente} · {programa.codigo} v{programa.version} · Racha dentro de meta: {estadoFase.rachaDentroMetaGeneral} mes(es)</p>
              {estadoSerie.ventanaGraciaSerieB.activa && <p style={{ fontSize: "12px", color: "#e63946", fontWeight: "700", margin: "8px 0 0" }}>⏳ Ventana de gracia Serie B (proveedor-cliente completo): {estadoSerie.ventanaGraciaSerieB.mesesRestantes} mes(es) restantes</p>}
              {estadoFase.alertaRiesgoBidding.activa && <p style={{ fontSize: "12px", color: "#f59e0b", fontWeight: "700", margin: "8px 0 0" }}>⚠️ Riesgo de pérdida de volumen bidding: {estadoFase.alertaRiesgoBidding.indicadores.join(", ")}</p>}
              {!detalle.participaPrograma && <p style={{ fontSize: "12px", color: subtexto, margin: "8px 0 0", fontStyle: "italic" }}>No participa del programa — fase fija en Bronce, evaluación por historial individual de SKU</p>}
            </div>
          )}
          {!programa && <div style={{ ...card, background: oscuro ? "#1a1a00" : "#fffbeb", border: "1px solid #f59e0b40" }}>
            <p style={{ fontSize: "13px", color: "#f59e0b" }}>Esta familia no tiene un programa de calidad de cliente asignado. Asignalo en la pestaña "Datos".</p>
          </div>}

          {/* Tabs */}
          <div style={{ display: "flex", gap: "2px", padding: "8px 0", marginBottom: "12px" }}>
            {[["datos", "📋 Datos"], ["indicadores", "📊 Indicadores mensuales"]].map(([id, lb]) => (
              <button key={id} onClick={() => setTabDetalle(id)}
                style={{ padding: "7px 14px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer", background: tabDetalle === id ? "#7B5BF2" : cardBg, color: tabDetalle === id ? "white" : subtexto, border: `1px solid ${tabDetalle === id ? "#7B5BF2" : borde}` }}>{lb}</button>
            ))}
          </div>

          {tabDetalle === "datos" && (<>
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
              {[["Código", detalle.codigo], ["Familia", detalle.nombre], ["Línea", detalle.linea], ["Cliente principal", detalle.clientePrincipal], ["% Procesos cubiertos", detalle.procesosCubiertos ? `${detalle.procesosCubiertos}%` : null], ["Creado por", detalle.creadoPor]].map(([k, v]) => v && (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
                  <span style={{ color: subtexto }}>{k}</span><span style={{ color: texto, fontWeight: "500" }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={card}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "12px" }}>Programa de calidad de cliente</h3>
              <span style={lbl}>Programa asignado</span>
              <select value={detalle.idProgramaCalidadCliente || ""} disabled={!esAdmin}
                onChange={e => actualizarFamilia(detalle.id, { idProgramaCalidadCliente: e.target.value ? Number(e.target.value) : null })} style={inp}>
                <option value="">— Ninguno —</option>
                {programas.map(p => <option key={p.id} value={p.id}>{p.cliente} — {p.codigo} v{p.version}</option>)}
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px" }}>
                <input type="checkbox" checked={detalle.participaPrograma !== false} disabled={!esAdmin}
                  onChange={e => actualizarFamilia(detalle.id, { participaPrograma: e.target.checked })} />
                <label style={{ fontSize: "13px", color: texto }}>Participa del programa (motor de fases activo)</label>
              </div>
              <span style={lbl}>Meta Anual de ppm (pactada con el cliente — pendiente de recibir)</span>
              <input type="number" defaultValue={detalle.metaAnualPpm ?? ""} disabled={!esAdmin} placeholder="sin dato todavía"
                onBlur={e => actualizarFamilia(detalle.id, { metaAnualPpm: e.target.value ? Number(e.target.value) : null })} style={inp} />
              <p style={{ fontSize: "11px", color: subtexto, marginTop: "4px" }}>El target real de ppm de cada mes se calcula como Meta Anual × (1 − % de reducción de la fase). Sin este dato, ppm se trata como "dentro de meta".</p>
            </div>
            <div style={card}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "12px" }}>Estado</h3>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {Object.entries(estadosFamilia).map(([val, { label, color }]) => (
                  <button key={val} disabled={!esAdmin} onClick={() => esAdmin && actualizarFamilia(detalle.id, { estado: val })}
                    style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: esAdmin ? "pointer" : "default", fontWeight: "600", border: detalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.estado === val ? `${color}22` : cardBg, color: detalle.estado === val ? color : subtexto }}>{label}</button>
                ))}
              </div>
            </div>
            {detalle.descripcion && <div style={card}><h3 style={{ fontSize: "13px", fontWeight: "600", color: "#09184D", marginBottom: "8px" }}>Descripción</h3><p style={{ fontSize: "13px", color: texto, lineHeight: "1.6", margin: 0 }}>{detalle.descripcion}</p></div>}
          </>)}

          {tabDetalle === "indicadores" && programa && (<>
            {esAdmin && (
              <div style={card}>
                <h3 style={{ fontSize: "13px", fontWeight: "700", color: texto, marginBottom: "10px" }}>Cargar indicadores del mes</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <div>
                    <span style={lbl}>Período</span>
                    <input type="month" value={formIndicadores.periodo} onChange={e => setFormIndicadores(f => ({ ...f, periodo: e.target.value }))} style={inp} />
                  </div>
                  <div>
                    <span style={lbl}>ppm rechazo</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <input type="number" value={formIndicadores.ppm ?? ""} onChange={e => setFormIndicadores(f => ({ ...f, ppm: e.target.value }))} style={inp} />
                      <button onClick={() => { const sug = sugerirPpm(detalle.id, formIndicadores.periodo); if (sug === null) alert("No hay datos de producción para ese período/familia."); else setFormIndicadores(f => ({ ...f, ppm: sug })); }}
                        style={{ padding: "6px 10px", background: "#eff6ff", border: "1px solid #bde0fe", borderRadius: "8px", cursor: "pointer", fontSize: "11px", color: "#3b82f6", whiteSpace: "nowrap", marginTop: "4px" }}>Sugerir</button>
                    </div>
                  </div>
                  <div><span style={lbl}>% Auditoría técnica</span><input type="number" value={formIndicadores.auditoriaTecnica ?? ""} onChange={e => setFormIndicadores(f => ({ ...f, auditoriaTecnica: e.target.value }))} style={inp} /></div>
                  <div><span style={lbl}>Cpk</span><input type="number" step="any" value={formIndicadores.cpk ?? ""} onChange={e => setFormIndicadores(f => ({ ...f, cpk: e.target.value }))} style={inp} /></div>
                  <div><span style={lbl}>% Cumplimiento RNC</span><input type="number" value={formIndicadores.cumplimientoRNC ?? ""} onChange={e => setFormIndicadores(f => ({ ...f, cumplimientoRNC: e.target.value }))} style={inp} /></div>
                  <div><span style={lbl}>Reincidencia RNC</span><input type="number" value={formIndicadores.reincidenciaRNC ?? ""} onChange={e => setFormIndicadores(f => ({ ...f, reincidenciaRNC: e.target.value }))} style={inp} /></div>
                </div>
                <button onClick={() => handleCerrarMes(detalle)} style={{ width: "100%", marginTop: "12px", padding: "11px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "14px", fontWeight: "700" }}>🔒 Cerrar mes {formIndicadores.periodo}</button>
                <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                  <button onClick={() => handleDesvioCritico(detalle)} style={{ flex: 1, padding: "9px", background: "none", border: "1px solid #e63946", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "#e63946", fontWeight: "600" }}>⚠️ Desvío crítico</button>
                  <button onClick={() => handleResultadoMonitoreo(detalle)} style={{ flex: 1, padding: "9px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: texto }}>📋 Resultado auditoría monitoreo</button>
                </div>
              </div>
            )}

            <div style={card}>
              <h3 style={{ fontSize: "13px", fontWeight: "700", color: texto, marginBottom: "10px" }}>Histórico de meses</h3>
              {historialIndicadores.length === 0
                ? <p style={{ fontSize: "12px", color: subtexto, textAlign: "center", padding: "1rem" }}>Sin meses cargados</p>
                : historialIndicadores.map(fila => (
                    <div key={fila.id} style={{ padding: "10px 0", borderBottom: `0.5px solid ${borde}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: "13px", fontWeight: "700", color: texto }}>{fila.periodo}</span>
                        <span style={{ fontSize: "11px", fontWeight: "600", color: fila.cerrado ? "#22c55e" : "#f59e0b" }}>{fila.cerrado ? "🔒 Cerrado" : "🔓 Reabierto"}</span>
                      </div>
                      <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0" }}>
                        ppm {fila.ppm ?? "—"} · Auditoría {fila.auditoriaTecnica ?? "—"}% · Cpk {fila.cpk ?? "—"} · Cumpl. RNC {fila.cumplimientoRNC ?? "—"}% · Reincid. {fila.reincidenciaRNC ?? "—"}
                      </p>
                      {fila.advertenciaFaseNoRecalculada && (
                        <div style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: "8px", padding: "8px 10px", margin: "6px 0" }}>
                          <p style={{ fontSize: "11px", color: "#92400e", fontWeight: "700", margin: 0 }}>⚠️ Este mes fue corregido tras reapertura — la fase NO se recalculó automáticamente. Revisala manualmente.</p>
                          {esAdmin && <button onClick={() => handleMarcarFaseRevisada(fila)} style={{ marginTop: "6px", fontSize: "10px", padding: "3px 8px", background: "none", border: "1px solid #92400e", borderRadius: "6px", cursor: "pointer", color: "#92400e" }}>Ya la revisé</button>}
                        </div>
                      )}
                      {esAdmin && (fila.cerrado
                        ? <button onClick={() => handleReabrirMes(fila)} style={{ fontSize: "11px", padding: "4px 10px", background: "none", border: `1px solid ${borde}`, borderRadius: "6px", cursor: "pointer", color: subtexto }}>Reabrir</button>
                        : <button onClick={() => handleCerrarFilaReabierta(fila)} style={{ fontSize: "11px", padding: "4px 10px", background: "#22c55e22", border: "1px solid #22c55e", borderRadius: "6px", cursor: "pointer", color: "#22c55e" }}>Cerrar de nuevo</button>)}
                    </div>
                  ))
              }
            </div>

            <div style={card}>
              <h3 style={{ fontSize: "13px", fontWeight: "700", color: texto, marginBottom: "10px" }}>Historial de fase</h3>
              {(estadoFase?.historialFases || []).length === 0
                ? <p style={{ fontSize: "12px", color: subtexto, textAlign: "center", padding: "1rem" }}>Sin cambios de fase</p>
                : [...estadoFase.historialFases].reverse().map((h, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: `0.5px solid ${borde}`, fontSize: "12px" }}>
                      <span style={{ fontWeight: "700", color: coloresFase[h.fase] }}>{iconosFase[h.fase]} {h.fase}</span>
                      <span style={{ color: subtexto }}> — {h.fecha} — {h.motivo}</span>
                    </div>
                  ))
              }
            </div>
          </>)}
          {tabDetalle === "indicadores" && !programa && (
            <p style={{ fontSize: "13px", color: subtexto, textAlign: "center", padding: "2rem" }}>Asigná un programa de calidad de cliente en la pestaña "Datos" primero.</p>
          )}
        </div>
      </div>
    );
  }

  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nueva Familia / Línea" onBack={() => setVista("listado")} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={card}>
          <span style={lbl}>Nombre de la familia</span><input onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} style={inp} />
          <span style={lbl}>Línea de producto</span><input onChange={e => setForm(f => ({ ...f, linea: e.target.value }))} style={inp} />
          <span style={lbl}>Cliente principal</span><input onChange={e => setForm(f => ({ ...f, clientePrincipal: e.target.value }))} style={inp} />
          <span style={lbl}>% Procesos cubiertos</span><input type="number" min="0" max="100" onChange={e => setForm(f => ({ ...f, procesosCubiertos: e.target.value }))} style={inp} placeholder="0-100" />
          <span style={lbl}>Meta Anual de ppm (si ya la pasó el cliente)</span>
          <input type="number" onChange={e => setForm(f => ({ ...f, metaAnualPpm: e.target.value ? Number(e.target.value) : null }))} style={inp} placeholder="sin dato todavía" />
          <span style={lbl}>Programa de calidad de cliente</span>
          <select onChange={e => setForm(f => ({ ...f, idProgramaCalidadCliente: e.target.value ? Number(e.target.value) : null }))} style={inp} defaultValue="">
            <option value="">— Ninguno —</option>
            {programas.map(p => <option key={p.id} value={p.id}>{p.cliente} — {p.codigo} v{p.version}</option>)}
          </select>
          <span style={lbl}>Estado</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {Object.entries(estadosFamilia).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, estado: val }))}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: form.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.estado === val ? `${color}22` : cardBg, color: form.estado === val ? color : texto }}>{label}</button>
            ))}
          </div>
          <span style={lbl}>Descripción</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={{ ...inp, resize: "none" }} />
        </div>
        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer" }}>Guardar Familia</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="familias" oscuro={oscuro} />
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Familias y Líneas</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {esAdmin && seleccionadas.size > 0 && <button onClick={() => setConfirmEliminarMultiple(true)} style={{ padding: "6px 14px", background: "#e63946", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>🗑 Borrar seleccionadas ({seleccionadas.size})</button>}
          {esAdmin && <button onClick={() => setVista("config")} style={{ padding: "6px 14px", background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>⚙️ Programas</button>}
          {esAdmin && <button onClick={() => setVista("nueva")} style={{ padding: "6px 14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva</button>}
        </div>
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        {colisionesMigracion.length > 0 && (
          <div style={{ background: "#fff5f5", border: "2px solid #e63946", borderRadius: "12px", padding: "14px 16px", marginBottom: "1rem" }}>
            <p style={{ fontSize: "13px", fontWeight: "700", color: "#e63946", margin: "0 0 8px" }}>⚠️ {colisionesMigracion.length} família(s) con el mismo nombre+cliente pero datos distintos entre este navegador y Supabase</p>
            <p style={{ fontSize: "12px", color: "#7f1d1d", margin: "0 0 8px" }}>No se sobreescribió nada — resolvé manualmente cuál versión es la correcta antes de seguir cargando indicadores para estas familias.</p>
            {colisionesMigracion.map((c, i) => (
              <p key={i} style={{ fontSize: "11px", color: "#7f1d1d", margin: "2px 0" }}>• {c.local.nombre} ({c.local.clientePrincipal || "sin cliente"}) — local id {c.local.id} vs Supabase id {c.remota.id}</p>
            ))}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total", items.length, "#7B5BF2"], ["Activas", items.filter(i => i.estado === "activa").length, "#22c55e"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        {items.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}><p style={{ fontSize: "40px" }}>🏭</p><p style={{ fontSize: "15px", color: texto }}>No hay familias registradas</p></div>
          : items.map(item => {
              const prog = programaDe(item);
              const fase = prog ? obtenerOCrearFase(item.id, prog.id) : null;
              return (
                <div key={item.id} onClick={() => { setDetalle(item); setTabDetalle("datos"); setVista("detalle"); }}
                  style={{ background: cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${seleccionadas.has(item.id) ? "#e63946" : borde}`, marginBottom: "10px", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(123,91,242,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                      {esAdmin && (
                        <input type="checkbox" checked={seleccionadas.has(item.id)} onClick={e => e.stopPropagation()}
                          onChange={() => toggleSeleccion(item.id)} style={{ marginTop: "3px", width: "15px", height: "15px", cursor: "pointer" }} />
                      )}
                      <div>
                        <span style={{ fontWeight: "700", color: "#7B5BF2", fontSize: "13px" }}>{item.codigo}</span>
                        <span style={{ marginLeft: "10px", fontSize: "13px", color: texto, fontWeight: "600" }}>{item.nombre || "Sin nombre"}</span>
                        <p style={{ fontSize: "12px", color: subtexto, marginTop: "4px" }}>{item.linea || "Sin línea"} · {item.clientePrincipal || "—"}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                      {fase && <span style={{ fontSize: "11px", fontWeight: "700", color: coloresFase[fase.faseActual] }}>{iconosFase[fase.faseActual]} {fase.faseActual} · Serie {estadoSerieDe(prog).serie}</span>}
                      <span style={{ fontSize: "11px", fontWeight: "700", color: estadosFamilia[item.estado]?.color }}>{estadosFamilia[item.estado]?.label}</span>
                    </div>
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
